import type {
  GridLayoutItem,
  GridLayoutItemInit,
  GridLayouts,
  NatureKPIState,
  RemoteViewsState,
  RemoteViewSummary,
  SinkConfig,
  SinkId,
  SinkPanelId,
  SourceAction,
  SourceConfig,
  SourceId,
  SourceSharedState,
  SourceState,
  SourceStatus,
  SpinSelection,
  Trait,
  TraitState,
  VizContext,
  VizSourceEntity
} from './VisualizationSliceTypes.js'
import type { VizScope } from './VisualizationSliceTypes.js'
import { cubeKey } from '../../../Repository/Visualization/CubeKey.js'
import type { Draft, PayloadAction } from '@reduxjs/toolkit'
import { createAction, createSlice } from '@reduxjs/toolkit'
import type { SampleBucket } from '../../../View/Component/SampleGrouping/OperationTypes.js'
import { BlastSource } from '../../../Model/Project/BlastSource.js'
import type { SerializableFieldsIndex } from './VisualizationSerializableFields.js'
import type { LoadSavedVizAction, SavedViz } from '../../../View/Component/ConfigurableVisualization/Persistence/SavedViz.js'
import { SINK_REGISTRY } from '../../../View/Component/ConfigurableVisualization/Sinks/SinkRegistry.js'
// react-grid-layout v2 renamed v1's single-item `Layout` type to `LayoutItem`.
import type { LayoutItem } from 'react-grid-layout'
import { removeRequest } from 'Flux/FluxUtils.ts'

export type SourceInitial = {
  name?: string
  config?: SourceConfig
  shared?: SourceSharedState
  /** Seeds `pinnedShared` on a NEW source (like `shared`) — used on reload to restore a persisted pinned view
   *  so "restore to pinned" works before the user re-pins in this session. */
  pinnedShared?: SourceSharedState
  state?: SourceState
  policy?: VizSourceEntity['policy'] // e.g. { mode: 'block' | 'progressive-structure' | 'progressive-values' }
}

/**
 * Status of a cube fetch for one `(scopeId, blastType)` tuple. The actual decoded
 * Arrow `Table` lives in `Repository/Visualization/CubeTableRegistry.ts` — see the
 * comment there for why it sits outside Redux state.
 */
export type CubeStatus = 'loading' | 'ready' | 'error'

export type CubeEntry = {
  status: CubeStatus
  /** Weak ETag returned by the backend; `null` if the scope has no upstream data yet. */
  etag: string | null
  /** Epoch ms when this entry was last materialised (fresh fetch or 304 hit). */
  loadedAt: number | null
  /** Present when `status === 'error'`. */
  error?: string
}

/** Key used to index cubes — see `cubeStateKey()`. */
export type CubeStateKey = string

export interface VisualizationState {
  sources: Record<SourceId, VizSourceEntity>
  version: 'viz-v1'
  openRequests: string[]
  context?: VizContext
  grid: { layouts: GridLayouts }
  __restoring: boolean
  /** Autosave is suspended so the previous session survives in storage until the user edits or resumes. */
  autosaveHeld: boolean
  remote: { views: RemoteViewsState }
  scope: VizScope | null
  traits: TraitState
  /** One entry per `(scopeId, blastType)`. Decoded tables are in the side registry. */
  cubes: Record<CubeStateKey, CubeEntry>
}

/** Build the slice key for a cube. Delegates to the shared `cubeKey` (registry/cache use the same). */
export const cubeStateKey = (scopeId: string, blastType: BlastSource, spin?: SpinSelection): CubeStateKey =>
  cubeKey(scopeId, blastType, spin)

/**
 * Epic-only trigger to *background-prefetch* a sibling blast type. Deliberately has NO reducer:
 * prefetch only warms the IndexedDB byte cache (`CubePrefetchLoaderEpic`) — it must not create a
 * `cubes[...]` entry, because a prefetched cube is neither `loading` (it's not being viewed) nor
 * `ready` (no decoded table is held resident). Marking the slice would either spin a stuck
 * spinner or, worse, claim `ready` for a key with no table. The foreground `cubeRequested` path
 * is what registers the decoded table when the user actually switches to that blast type.
 *
 * @see CubePrefetchEpic (emits this) / CubePrefetchLoaderEpic (handles it)
 */
export const cubePrefetchRequested = createAction<{ scopeId: string; blastType: BlastSource }>(
  'visualization/cubePrefetchRequested'
)

export const initialState: VisualizationState = {
  openRequests: [],
  sources: {},
  version: 'viz-v1',
  grid: { layouts: { lg: [], md: [], sm: [], xs: [], xxs: [] } },
  __restoring: false,
  autosaveHeld: false,
  remote: { views: { items: [], loading: false, error: undefined } },
  scope: null,
  traits: {
    traits: undefined,
    taxonTraitMap: undefined,
    loadingState: 'initializing',
    traitGroups: undefined
  },
  cubes: {}
}

const defaultSourceConfig = (): SourceConfig => ({
  kind: 'pipeline/v1',
  pipeline: { filters: [], groupBys: [], projections: [], sortBys: [] }
})

/**
 * Ensure a VizSourceEntity exists for sourceId.
 * - Lazily creates a source with sane defaults the first time it’s touched.
 * - Centralizes default shape so reducers can stay “dumb”.
 * - Lets UI/epics dispatch source actions without a prior explicit register.
 */
function ensureSource(state: Draft<VisualizationState>, sourceId: SourceId): VizSourceEntity {
  let src = state.sources[sourceId]
  if (!src) {
    src = state.sources[sourceId] = {
      id: sourceId,
      name: 'new source',
      config: defaultSourceConfig(),
      shared: {
        blastType: BlastSource.BOLD
      },
      state: { selectedBucketKeys: [] },
      status: 'init',
      error: undefined,
      sourceKey: '',
      snapshot: null,
      sinks: {},
      policy: {
        mode: 'block'
      }
    }
  }
  return src
}

const BPS = ['lg', 'md', 'sm', 'xs', 'xxs'] as const

export function filterLayoutsToPanels(layouts: GridLayouts, allowed: Set<string>): GridLayouts {
  const next: GridLayouts = { lg: [], md: [], sm: [], xs: [], xxs: [] }
  for (const bp of BPS) {
    const arr = (layouts[bp] ?? []) as LayoutItem[]
    next[bp] = arr.filter((item) => allowed.has(item.i))
  }
  return next
}

function removePanels(layouts: GridLayouts, predicate: (i: GridLayoutItem) => boolean) {
  const next: GridLayouts = { ...layouts }
  for (const bp of Object.keys(next) as Array<keyof GridLayouts>) {
    next[bp] = (next[bp] || []).filter((i) => !predicate(i))
  }
  return next
}

function selectBuckets(allBuckets: SampleBucket[], selectedKeys: string[] | undefined) {
  if (!selectedKeys || selectedKeys.length === 0) return allBuckets
  const set = new Set(selectedKeys)
  return allBuckets.filter((b) => set.has(b.key))
}

export const reducers = {
  traitStateError: (state: Draft<VisualizationState>, action: PayloadAction<{ error: unknown; scopeId?: string }>) => {
    const { scopeId } = action.payload
    if (scopeId !== undefined && state.scope !== null && scopeId !== state.scope.id) return

    console.error('error loading traits', action.payload.error)
    state.traits.loadingState = 'error'
    state.openRequests = removeRequest(state, action)
  },
  /** `scopeId` is consumed by TraitLoadingEpic, not here. */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  loadTraits: (state: Draft<VisualizationState>, _action: PayloadAction<{ scopeId: string }>) => {
    state.traits.loadingState = 'loading'
  },
  /**
   * Guarded by scopeId against `state.scope` so a response from a since-superseded scope can't
   * silently overwrite the current one. A null `state.scope` (the reset()-then-setScope() window
   * VizLoaderOrderPage/VizLoaderPage pass through on scope switch) is accepted rather than
   * rejected — rejecting here would strand `loadingState` on 'loading' forever and reintroduce
   * the permanently-spinning panel S2-1051 fixed.
   */
  traitsLoaded: (
    state: Draft<VisualizationState>,
    action: PayloadAction<{ traits: Trait[]; traitMap: Record<string, Trait[]>; traitGroups: string[]; scopeId: string }>
  ) => {
    if (state.scope !== null && action.payload.scopeId !== state.scope.id) return

    state.traits.loadingState = 'ready'
    state.traits.traits = action.payload.traits
    state.traits.taxonTraitMap = action.payload.traitMap
    state.traits.traitGroups = action.payload.traitGroups

    console.info('loaded traits', action.payload.traits.length)
  },
  setScope: (state: Draft<VisualizationState>, action: PayloadAction<VizScope>) => {
    state.scope = action.payload
  },
  /**
   * Trigger action: ask the cube loader epic to fetch `(scopeId, blastType)`. Also marks the
   * slice entry as `loading` so consumers can show a spinner immediately. The epic listens
   * for this action and may either issue a network request or short-circuit on a 304 hit
   * against the IndexedDB-cached ETag.
   */
  cubeRequested: (
    state: Draft<VisualizationState>,
    action: PayloadAction<{ scopeId: string; blastType: BlastSource; spin?: SpinSelection }>
  ) => {
    const key = cubeStateKey(action.payload.scopeId, action.payload.blastType, action.payload.spin)
    const prev = state.cubes[key]
    state.cubes[key] = {
      status: 'loading',
      etag: prev?.etag ?? null,
      loadedAt: prev?.loadedAt ?? null
    }
  },
  /**
   * A fresh cube has been fetched and decoded; the table has already been put into the side
   * registry by the epic. Selectors should look up the table from there once `status === 'ready'`.
   */
  cubeReady: (
    state: Draft<VisualizationState>,
    action: PayloadAction<{ key: CubeStateKey; etag: string | null; loadedAt: number }>
  ) => {
    const { key, etag, loadedAt } = action.payload
    state.cubes[key] = { status: 'ready', etag, loadedAt }
  },
  /**
   * `If-None-Match` returned 304 — the locally-known cube is still current. Bump `loadedAt`
   * so callers waiting on "fresh-enough" can proceed; etag stays the same.
   */
  cubeUnchanged: (
    state: Draft<VisualizationState>,
    action: PayloadAction<{ key: CubeStateKey; loadedAt: number }>
  ) => {
    const { key, loadedAt } = action.payload
    const prev = state.cubes[key]
    if (!prev) return
    state.cubes[key] = { ...prev, status: 'ready', loadedAt }
  },
  cubeError: (
    state: Draft<VisualizationState>,
    action: PayloadAction<{ key: CubeStateKey; error: string }>
  ) => {
    const { key, error } = action.payload
    const prev = state.cubes[key]
    state.cubes[key] = {
      status: 'error',
      etag: prev?.etag ?? null,
      loadedAt: prev?.loadedAt ?? null,
      error
    }
  },
  setRestoring: (state: Draft<VisualizationState>, action: PayloadAction<{ restoring: boolean }>) => {
    state.__restoring = action.payload.restoring
  },
  holdAutosave: (state: Draft<VisualizationState>) => {
    state.autosaveHeld = true
  },
  releaseAutosaveHold: (state: Draft<VisualizationState>) => {
    state.autosaveHeld = false
  },
  updateGridLayouts: (state: Draft<VisualizationState>, action: PayloadAction<{ layouts: GridLayouts }>) => {
    state.grid.layouts = action.payload.layouts
  },
  renamePanel: (state: Draft<VisualizationState>, action: PayloadAction<{ sourceId: SourceId; sinkId: SinkId; name: string }>) => {
    const { sourceId, sinkId, name } = action.payload
    if (state.sources[sourceId]?.sinks[sinkId]?.config?.title === undefined) {
      console.warn("Couldn't find sink to edit")
      return
    }

    state.sources[action.payload.sourceId].sinks[action.payload.sinkId].config.title = name
  },
  addPanelsToLayouts: (
    state: Draft<VisualizationState>,
    action: PayloadAction<{ panelIds?: SinkPanelId[]; items?: GridLayoutItemInit[] }>
  ) => {
    const { panelIds, items } = action.payload

    const add = (bpItems: GridLayoutItem[]) => {
      const existing = new Set(bpItems.map((i) => i.i))

      // Prefer items with per-panel sizes if provided
      const prepared = (items ?? [])
        .filter((it) => !existing.has(it.i))
        .map((it, idx) => {
          const w = it.w ?? 4
          const h = it.h ?? 14
          const x = it.x ?? ((bpItems.length + idx) * 4) % 12
          const y = it.y ?? Infinity
          if (import.meta.env.DEV && (!w || !h)) {
            console.warn(`[grid] BAD INSERT`, { it, computed: { w, h, x } })
          }
          return {
            i: it.i,
            x,
            y,
            w,
            h,
            minW: it.minW ?? 3,
            minH: it.minH ?? 14,
            maxW: it.maxW,
            maxH: it.maxH,
            static: it.static
          }
        })

      // Fallback for legacy callers with just panelIds
      const toAddFromIds =
        panelIds
          ?.filter((id) => !existing.has(id))
          .map(
            (pid, idx) =>
              ({
                i: pid,
                x: ((bpItems.length + prepared.length + idx) * 4) % 12,
                y: Infinity,
                w: 4,
                h: 14,
                minW: 3,
                minH: 14
              }) as GridLayoutItem
          ) ?? []

      return [...bpItems, ...prepared, ...toAddFromIds]
    }

    const next: GridLayouts = { ...state.grid.layouts }
    for (const bp of Object.keys(next) as Array<keyof GridLayouts>) {
      next[bp] = add(next[bp] || [])
    }
    state.grid.layouts = next
  },

  removePanelsBySink: (state: Draft<VisualizationState>, action: PayloadAction<{ sinkId: SinkId }>) => {
    const prefix = `${action.payload.sinkId}:`
    state.grid.layouts = removePanels(state.grid.layouts, (i) => i.i === action.payload.sinkId || i.i.startsWith(prefix))
  },
  removeSource: (state: Draft<VisualizationState>, action: PayloadAction<SourceAction>) => {
    delete state.sources[action.payload.sourceId]
  },
  loadSavedViz: (state: Draft<VisualizationState>, action: PayloadAction<LoadSavedVizAction>) => {
    const { saved, asTemplate } = action.payload
    state.autosaveHeld = false

    // (optional) version guard
    if (saved.version !== 'viz-v1') {
      // ignore or migrate — up to you
    }

    // Remove any sources that aren't part of the saved object. `loadSavedViz` replaces the
    // workspace (Apply saved view / restore remote view / load default), so sources left over
    // from the previously open workspace must not linger alongside the restored ones.
    for (const sourceId of Object.keys(state.sources)) {
      if (!(sourceId in saved.sources)) delete state.sources[sourceId]
    }

    // Recreate sources from the saved object (normalize + defaults)
    for (const [sourceId, srcSaved] of Object.entries(saved.sources)) {
      const existing = state.sources[sourceId]
      const next = ensureSource(state, sourceId) // gets defaults if not there

      next.name = srcSaved.name ?? existing?.name ?? 'untitled source'
      next.config = srcSaved.config ?? existing?.config ?? next.config
      next.shared = { ...next.shared, ...(srcSaved.shared ?? {}) }
      next.policy = existing?.policy ?? next.policy

      // Template: wipe selection; Saved-view: keep it
      next.state = {
        selectedBucketKeys: asTemplate ? [] : (srcSaved?.selectedBucketKeys ?? existing?.state?.selectedBucketKeys ?? [])
      }

      // We want a clean recompute/load
      next.status = 'init'
      next.error = undefined
      next.sourceKey = ''
      next.snapshot = null // force pipeline & datasets to recompute

      // Sinks (just copy)
      next.sinks = srcSaved.sinks ?? existing?.sinks ?? {}
    }

    // 1) Start from saved layouts (or empty)
    const savedLayouts = saved.grid?.layouts ?? { lg: [], md: [], sm: [], xs: [], xxs: [] }

    // 2) Compute which panels exist *now* (based on sinks + selected buckets)
    const allowed = new Set<string>()
    for (const [, src] of Object.entries(state.sources)) {
      const sinks = Object.values(src.sinks).map((s) => s.config)
      for (const sink of sinks) {
        const reg = SINK_REGISTRY[sink.kind as keyof typeof SINK_REGISTRY]
        if (!reg) continue
        if (reg.renderMode === 'multi-bucket') {
          allowed.add(sink.id) // single panel per sink
        } else {
          /*
           * A single-bucket sink renders one panel per selected bucket, keyed `${sink.id}:${bucketKey}`
           * (SinksGrid). Those ids were previously left out on the grounds that the buckets are not known
           * yet — but the selection has just been restored above, so they are. Omitting them meant the
           * saved layout was filtered down to whatever multi-bucket sinks existed, and a workspace built
           * from single-bucket sinks restored to an empty grid.
           */
          for (const bucketKey of src.state?.selectedBucketKeys ?? []) {
            allowed.add(`${sink.id}:${bucketKey}`)
          }
        }
      }
    }

    // 3) Accept the saved layouts for panels we know; others get default placement later
    state.grid.layouts = filterLayoutsToPanels(savedLayouts, allowed)
  },

  metadataFieldsIndexReady: (
    state: Draft<VisualizationState>,
    action: PayloadAction<SourceAction & { index: SerializableFieldsIndex }>
  ) => {
    const src = state.sources[action.payload.sourceId]
    if (!src) return
    src.snapshot = {
      ...(src.snapshot ?? { schema: 'buckets+taxa/v1', allBuckets: [], selectedBuckets: [], kronaByBucket: {}, meta: { pagesLoaded: -1 } }),
      metadataIndex: action.payload.index
    }
  },
  createSource: (state: Draft<VisualizationState>, action: PayloadAction<SourceAction & { initial?: SourceInitial }>) => {
    const { sourceId, initial } = action.payload

    const existing = state.sources[sourceId]
    // EXISTING wins: createSource CREATES a source or PRESERVES a live one — it must never clobber a source's
    // runtime state (e.g. the user's zoom / selectedPath) when a component re-mounts (maximize toggles the card
    // between grid + overlay) and re-dispatches it. `initial` seeds a NEW source only. Safe: VizWorkspace always
    // uses fresh random ids, and updateChart edits go through updateSourceShared, not here.
    const nextConfig = existing?.config ?? initial?.config ?? defaultSourceConfig()
    const nextShared = existing?.shared ?? initial?.shared ?? {}
    const nextPinnedShared = existing?.pinnedShared ?? initial?.pinnedShared
    const nextState: SourceState = existing?.state ?? initial?.state ?? { selectedBucketKeys: [] }
    const nextPolicy = existing?.policy ?? initial?.policy ?? { mode: 'block' as const }

    state.sources[sourceId] = {
      id: sourceId,
      name: initial?.name ?? 'untitled source',
      config: nextConfig,
      shared: nextShared,
      pinnedShared: nextPinnedShared,
      state: nextState,
      policy: nextPolicy,
      status: existing?.status ?? 'init',
      error: undefined, // clear any stale error on (re)create
      sourceKey: existing?.sourceKey ?? '',
      snapshot: existing?.snapshot ?? null,
      sinks: existing?.sinks ?? {}
    }
  },

  sourceStarted: (state: Draft<VisualizationState>, action: PayloadAction<SourceAction & { sourceKey: string }>) => {
    const { sourceId, sourceKey } = action.payload
    const src = state.sources[sourceId]
    if (!src) return
    src.sourceKey = sourceKey
  },
  // Source lifecycle ----------------------------------------------------------
  applySourceChanges: (
    state: Draft<VisualizationState>,
    action: PayloadAction<SourceAction & { config: SourceConfig; selectedKeys: string[] }>
  ) => {
    const { sourceId, config, selectedKeys } = action.payload
    const src = state.sources[sourceId] ?? (state.sources[sourceId] = ensureSource(state, sourceId))
    src.config = config
    src.state.selectedBucketKeys = selectedKeys
    if (config.defaults) src.shared = { ...config.defaults, ...src.shared }
  },
  setSourceConfig: (state: Draft<VisualizationState>, action: PayloadAction<SourceAction & { config: SourceConfig }>) => {
    const { sourceId, config } = action.payload
    const src = ensureSource(state, sourceId)
    src.config = config
    if (config.defaults) src.shared = { ...config.defaults, ...src.shared }
  },

  setSourceState: (state: Draft<VisualizationState>, action: PayloadAction<SourceAction & { state: SourceState }>) => {
    const { sourceId, state: st } = action.payload
    const src = ensureSource(state, sourceId)
    src.state = st
  },

  updateNatureKpis: (
    state: Draft<VisualizationState>,
    action: PayloadAction<SourceAction & { state: Partial<NatureKPIState> | undefined }>
  ) => {
    const src = ensureSource(state, action.payload.sourceId)

    if (action.payload.state === undefined) {
      src.shared.natureKPIs = undefined
      return
    }

    const traitGroupToUse = action.payload.state?.traitGroup ?? src.shared.natureKPIs?.traitGroup ?? ''
    // traitName is REPLACE (not merge): picking a group header clears it, picking a trait sets it.
    const traitNameToUse = action.payload.state?.traitName
    if (traitGroupToUse === '' && (traitNameToUse === undefined || traitNameToUse === '')) {
      console.error('unacceptable state: a trait group or a trait name must be set')
      src.shared.natureKPIs = undefined
    } else {
      src.shared.natureKPIs = {
        traitGroup: traitGroupToUse,
        traitName: traitNameToUse === '' ? undefined : traitNameToUse,
        noTraitMode: action.payload.state?.noTraitMode ?? src.shared.natureKPIs?.noTraitMode ?? 'include'
      }
    }
  },

  updateSourceShared: (
    state: Draft<VisualizationState>,
    action: PayloadAction<{ sourceId: SourceId; patch: Partial<SourceSharedState> }>
  ) => {
    const { sourceId, patch } = action.payload
    const src = ensureSource(state, sourceId)
    src.shared = { ...src.shared, ...patch }
    // NOTE: switching a cube-identity knob (blastType / spin) swaps the taxonomy tree, which can
    // leave `selectedPath` matching nothing under the new source. We deliberately do NOT reset it
    // here — `taxaPathRepairEpic` repairs (or resets) the path once the new cube is ready, so a
    // still-valid path is preserved and comparison across sources keeps the user's place.
  },

  cancelSource: (state: Draft<VisualizationState>, action: PayloadAction<SourceAction>) => {
    const { sourceId } = action.payload
    const src = ensureSource(state, sourceId)
    src.status = 'init'
    state.openRequests = removeRequest(state, action) // ⬅️ stop tracking
  },

  // Streaming updates ---------------------------------------------------------
  bucketsReady: (
    state: Draft<VisualizationState>,
    action: PayloadAction<
      SourceAction & {
        buckets: SampleBucket[]
        selectedKeys: string[]
        meta?: { pagesLoaded?: number; totalPages?: number; elapsedMs?: number; nSamples?: number }
      }
    >
  ) => {
    const { sourceId, buckets, selectedKeys, meta } = action.payload
    const src = ensureSource(state, sourceId)
    const selectedBuckets = selectBuckets(buckets, selectedKeys)
    const prev = src.snapshot
    src.snapshot = {
      schema: 'buckets+taxa/v1',
      allBuckets: buckets,
      selectedBuckets,
      meta: { ...(prev?.meta ?? { pagesLoaded: -1 }), ...(meta ?? { pagesLoaded: -1 }) },
      metadataIndex: prev?.metadataIndex ?? { staticKeys: [], customKeys: [] }
    }
    src.state.selectedBucketKeys = selectedKeys
    src.status = 'waiting'
  },

  sourceStatusChanged: (state: Draft<VisualizationState>, action: PayloadAction<SourceAction & { newStatus: SourceStatus }>) => {
    const { sourceId, newStatus } = action.payload
    const src = ensureSource(state, sourceId)
    src.status = newStatus
    state.openRequests = removeRequest(state, action)
  },

  sourceError: (state: Draft<VisualizationState>, action: PayloadAction<SourceAction & { error: unknown }>) => {
    const { sourceId, error } = action.payload
    const src = ensureSource(state, sourceId)
    src.status = 'error'
    src.error = typeof error === 'string' ? error : JSON.stringify(error)
    state.openRequests = removeRequest(state, action)
  },

  updateSourceName: (state: Draft<VisualizationState>, action: PayloadAction<SourceAction & { name: string }>) => {
    const src = state.sources[action.payload.sourceId]
    if (!src) return
    src.name = action.payload.name
  },

  /**
   * Selected taxonomy zoom path. PURELY a view filter — does NOT trigger a backend re-fetch.
   *
   * DO NOT migrate this into `updateSourceShared` and DO NOT add `selectedPath` to the
   * `serverAffectingKnobs` in KronaStreamingEpic. The current design relies on the FE filtering
   * the already-loaded data; pushing `selectedPath` server-side would force a round-trip on
   * every zoom click — exactly the latency the cube design avoids.
   */
  /** `auto` marks a zoom the app made on its own (auto-descend, path repair), not a user drill. */
  setTaxaZoom: (state: Draft<VisualizationState>, action: PayloadAction<SourceAction & { path: string[]; auto?: boolean }>) => {
    const src = state.sources[action.payload.sourceId]
    if (!src) return
    src.shared = { ...src.shared, selectedPath: action.payload.path }
  },
  goBackOneTaxaLevel: (state: Draft<VisualizationState>, action: PayloadAction<SourceAction>) => {
    const src = state.sources[action.payload.sourceId]
    if (!src) return
    const path = src.shared.selectedPath ?? []
    if (path.length > 0) {
      src.shared = { ...src.shared, selectedPath: path.slice(0, -1) }
    }
  },
  resetTaxaZoom: (state: Draft<VisualizationState>, action: PayloadAction<SourceAction>) => {
    const src = state.sources[action.payload.sourceId]
    if (!src) return
    src.shared = { ...src.shared, selectedPath: [] }
  },
  /** Capture the current `shared` as the pinned-view snapshot (dispatched when the card is pinned). */
  snapshotPinnedShared: (state: Draft<VisualizationState>, action: PayloadAction<SourceAction>) => {
    const src = state.sources[action.payload.sourceId]
    if (src) src.pinnedShared = { ...src.shared }
  },
  /** Restore `shared` to the pinned-view snapshot (the "return to pinned state" action). No-op if none. */
  restorePinnedShared: (state: Draft<VisualizationState>, action: PayloadAction<SourceAction>) => {
    const src = state.sources[action.payload.sourceId]
    if (src?.pinnedShared) src.shared = { ...src.pinnedShared }
  },

  registerSink: (state: Draft<VisualizationState>, action: PayloadAction<SourceAction & { sinkconfig: SinkConfig }>) => {
    const { sourceId, sinkconfig: sinkconfig } = action.payload
    const src = ensureSource(state, sourceId)
    src.sinks[sinkconfig.id] = { config: sinkconfig }
  },

  unregisterSink: (state: Draft<VisualizationState>, action: PayloadAction<SourceAction & { sinkId: SinkId }>) => {
    const { sourceId, sinkId } = action.payload
    const src = ensureSource(state, sourceId)
    delete src.sinks[sinkId]
  },
  /* used by epic */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  remoteListRequest: (s: Draft<VisualizationState>, a: PayloadAction<{ scopeId: string }>) => {
    s.remote.views.loading = true
    s.remote.views.error = undefined
  },
  remoteListSuccess: (s: Draft<VisualizationState>, a: PayloadAction<{ items: RemoteViewSummary[] }>) => {
    s.remote.views.loading = false
    s.remote.views.items = a.payload.items
  },
  remoteListFailure: (s: Draft<VisualizationState>, a: PayloadAction<{ error: string }>) => {
    s.remote.views.loading = false
    s.remote.views.error = a.payload.error
  },
  remoteCreateRequest: (
    /* used by epic */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    s: Draft<VisualizationState>,
    /* used by epic */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    a: PayloadAction<{
      projectId?: string | null
      orderId?: string | null
      name: string
      comment: string | null
      isTemplate: boolean
      schemaVersion: string
      state: SavedViz
      shared: boolean
    }>
  ) => {},
  remoteCreateSuccess: (s: Draft<VisualizationState>, a: PayloadAction<{ item: RemoteViewSummary }>) => {
    // optimistic: add/update in list
    const idx = s.remote.views.items.findIndex((i) => i.id === a.payload.item.id)
    if (idx >= 0) s.remote.views.items[idx] = a.payload.item
    else s.remote.views.items.unshift(a.payload.item)
  },
  /* used by epic */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  remoteCreateFailure: (s: Draft<VisualizationState>, _a: PayloadAction<{ error: string }>) => {},
  /* used by epic */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  remoteDeleteRequest: (s: Draft<VisualizationState>, a: PayloadAction<{ id: string; modifiedAt: string }>) => {},
  remoteDeleteSuccess: (s: Draft<VisualizationState>, a: PayloadAction<{ id: string }>) => {
    s.remote.views.items = s.remote.views.items.filter((i) => i.id !== a.payload.id)
  },
  /* used by epic */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  remoteDeleteFailure: (s: Draft<VisualizationState>, _a: PayloadAction<{ error: string }>) => {},
  /* used by epic */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  remoteLoadRequest: (s: Draft<VisualizationState>, _a: PayloadAction<{ id: string; asTemplate: boolean }>) => {},
  /* used by epic */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  remoteLoadFailure: (s: Draft<VisualizationState>, _a: PayloadAction<{ error: string }>) => {},

  reset: () => initialState
}

export const visualizationSlice = createSlice({
  name: 'visualization',
  initialState,
  reducers
})
